import { ColumnFamily, SelectedFamily } from '@flexible-reports/store/metadata.types';

/**
 * Resolves deprecated field IDs to their current active identifiers.
 * Fields that are not deprecated are returned as-is.
 *
 * @param fieldIds list of field IDs that may contain deprecated identifiers
 * @param deprecatedIdsMap mapping of deprecated ID → current active ID
 * @return list of field IDs with deprecated ones replaced by current identifiers
 */
export const resolveDeprecatedFieldIds = (fieldIds: string[], deprecatedIdsMap: Record<string, string>): string[] => {
  return fieldIds.map(id => deprecatedIdsMap[id] ?? id);
};

/**
 * Flattens selected families by its family id and attributes
 *
 * @param selectedFamilies selected families to be flattened
 * @return flattened list of string
 */
export const flattenSelectedFamilies = (selectedFamilies: SelectedFamily[]): string[] => {
  return selectedFamilies.flatMap(family => [family.id, ...family.attributes]);
};

/**
 * Builds 'SelectedFamily' object for given ids and attribute to family id map
 *
 * @param selectedIds list of selected dimension or metric ids. e.g. 'campaign.name', 'metric.sales'
 * @param attributeToFamilyIdMap attribute to family id map. e.g. Map of ['campaign.name', 'campaign'], ['metric.sales', 'purchases']
 * @return list of SelectedFamily
 */
export const buildSelectedFamiliesByMap = (selectedIds: string[], attributeToFamilyIdMap: Map<string, string>): SelectedFamily[] => {
  const familyMap: Map<string, Set<string>> = new Map();

  for (const id of selectedIds) {
    const familyId = attributeToFamilyIdMap.get(id);

    if (familyId) {
      if (!familyMap.has(familyId)) {
        familyMap.set(familyId, new Set());
      }
      familyMap.get(familyId)?.add(id);
    } else {
      if (!familyMap.has(id)) {
        familyMap.set(id, new Set());
      }
    }
  }

  return Array.from(familyMap.entries()).map(([id, attributes]) => ({
    id,
    attributes: Array.from(attributes)
  }));
};

/**
 * Builds attribute to family id map for given column families
 *
 * @param columnFamilies column families (dimension or metric families)
 * @return attribute to family id map. e.g. Map of ['campaign.name', 'campaign'], ['metric.sales', 'purchases']
 */
export const columnAttributeToFamilyIdMap = (columnFamilies: ColumnFamily[]) => {
  return columnFamilies.reduce((acc, family) => {
    if (family.attributes) {
      family.attributes.forEach(attribute => {
        acc.set(attribute.id, family.id);
      });
    }
    return acc;
  }, new Map<string, string>());
};

/**
 * Adds missing family ids for given selected ids if they do not have
 *
 * @param selectedIds list of selected dimension or metric or family id. e.g. 'campaign.name', 'metric.sales' and 'campaign'
 * @param attributeToFamilyIdMap attribute to family id map. e.g. Map of ['campaign.name', 'campaign'], ['metric.sales', 'purchases']
 * @return updated selection of ids
 */
export const addMissingFamilyIds = (selectedIds: string[], attributeToFamilyIdMap: Map<string, string>): string[] => {
  const result: string[] = [];
  const addedParents = new Set<string>();

  for (const id of selectedIds) {
    const parentId = attributeToFamilyIdMap.get(id);
    if (parentId && !addedParents.has(parentId) && !selectedIds.includes(parentId)) {
      result.push(parentId);
      addedParents.add(parentId);
    }
    result.push(id);
  }
  return result;
};

/**
 * Builds selected families for selected ids using column family information
 *
 * @param columnFamilies column families (dimension families)
 * @param selectedIds list of selected dimension or metric ids. e.g. 'campaign.name', 'metric.sales'
 * @return list of SelectedFamily for Dimensions in the order of selected ids
 */
export const buildSelectedFamiliesForDimensions = (columnFamilies: ColumnFamily[], selectedIds: string[]): SelectedFamily[] => {
  const matchedFamilyMap = new Map<string, string[]>();
  // Process each selected ID in order by filtering dimensions
  selectedIds
    .filter(id => !id.startsWith('metric.'))
    .forEach(id => {
      const family = columnFamilies.find(family => family.id === id || family.attributes?.some(attr => attr.id === id));
      if (family) {
        if (family.attributes) {
          const matchedAttributes = family.attributes.filter(attr => selectedIds.includes(attr.id)).map(attr => attr.id);
          matchedFamilyMap.set(family.id, matchedAttributes);
        } else {
          matchedFamilyMap.set(family.id, []);
        }
      }
    });
  return Array.from(matchedFamilyMap, ([id, attributes]) => ({ id, attributes })) as SelectedFamily[];
};

/**
 * Builds selected families for selected ids using column family information
 *
 * @param columnFamilies column families (metric families)
 * @param selectedIds list of selected dimension or metric ids. e.g. 'campaign.name', 'metric.sales'
 * @return list of SelectedFamily for metrics in the order of selected ids
 */
export const buildSelectedFamiliesForMetrics = (columnFamilies: ColumnFamily[], selectedIds: string[]): SelectedFamily[] => {
  const matchedFamilyMap = new Map<string, string[]>();
  // Process each selected ID in order by filtering metrics
  selectedIds
    .filter(id => id.startsWith('metric.'))
    .forEach(id => {
      const family = columnFamilies.find(family => family.id === id || family.attributes?.some(attr => attr.id === id));
      if (family) {
        matchedFamilyMap.set(id, []);
      }
    });
  return Array.from(matchedFamilyMap, ([id, attributes]) => ({ id, attributes })) as SelectedFamily[];
};

/**
 * Transforms an array of metric IDs into an array of SelectedFamily objects.
 * Each ID becomes a SelectedFamily with empty attributes array.
 *
 * @param {string[]} orderedIds - Array of metric IDs in their desired order
 * @returns {SelectedFamily[]} Array of SelectedFamily objects, each containing an ID and empty attributes array
 *
 * @example
 * const ids = ['metric.revenueNet', 'metric.costFixed'];
 * const result = buildFlatSelectedFamilies(ids);
 * // Returns:
 * // [
 * //   { id: 'metric.revenueNet', attributes: [] },
 * //   { id: 'metric.costFixed', attributes: [] }
 * // ]
 */

export const buildFlatSelectedFamilies = (orderedIds: string[]): SelectedFamily[] => {
  return orderedIds.map(id => ({ id, attributes: [] }));
};
